import { useEffect, useState } from 'react';
import { Calendar, CheckCircle2, ListMusic, Pencil } from 'lucide-react';
import { Card } from '../../ui/Card';
import { PLAYLIST_NAME_MAX, defaultPlaylistName } from '../../../utils/playlist';
import { TONE_CLASSES } from '../../../utils/tones';
import { COPY } from './playlistCopy';

const ERROR_ID = 'playlist-name-error';
const V1_VISIBLE_MS = 3000;

/**
 * Title, service date, the saved-playlist controls and (≥768 px) the Deliver control. The title follows
 * the date while it is still the untouched default for that date.
 */
export default function PlaylistHeader({ name, serviceDate, readOnly, deliveredCaption, onMetaChange, savedControls, deliverControl }) {
  const [draftName, setDraftName] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (error !== COPY.nameEmpty) return undefined;
    const id = setTimeout(() => setError(null), V1_VISIBLE_MS);
    return () => clearTimeout(id);
  }, [error]);

  const handleNameChange = (event) => {
    const { value } = event.target;
    if (value.trim() === '') {
      setDraftName(value);
      return;
    }
    setDraftName(null);
    if (error === COPY.nameEmpty) setError(null);
    onMetaChange({ name: value });
  };

  const handlePaste = (event) => {
    const { selectionStart, selectionEnd, value } = event.currentTarget;
    const pasted = event.clipboardData.getData('text');
    const resulting = value.length - (selectionEnd - selectionStart) + pasted.length;
    setError(resulting > PLAYLIST_NAME_MAX ? COPY.nameTooLong : null);
  };

  const handleNameBlur = () => {
    if (draftName !== null) {
      setDraftName(null);
      setError(COPY.nameEmpty);
    } else if (error === COPY.nameTooLong) {
      setError(null);
    }
    if (name !== name.trim()) onMetaChange({ name: name.trim() });
  };

  const handleDateChange = (event) => {
    const next = event.target.value;
    if (!next) return;
    const followsDefault = name === defaultPlaylistName(serviceDate);
    onMetaChange({ serviceDate: next, ...(followsDefault ? { name: defaultPlaylistName(next) } : {}) });
  };

  return (
    <Card variant="elevated" className="mb-6">
      <div className="flex flex-col gap-5">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <div className="hidden min-[400px]:block p-3 squircle-sm bg-primary-100 dark:bg-primary-900/30 flex-shrink-0">
            <ListMusic className="w-6 h-6 text-primary-600 dark:text-primary-400" />
          </div>
          <div className="min-w-0 flex-1">
            <label htmlFor="playlist-name-input" className="sr-only">{COPY.nameLabel}</label>
            <div className="group relative inline-grid max-w-full align-top">
              <span
                aria-hidden="true"
                className="invisible col-start-1 row-start-1 -ml-2 pl-2 pr-11 py-1 whitespace-pre overflow-hidden text-heading md:text-heading-lg font-semibold"
              >
                {draftName || name}
              </span>
              <input
                id="playlist-name-input"
                type="text"
                value={draftName ?? name}
                maxLength={PLAYLIST_NAME_MAX}
                readOnly={readOnly}
                onChange={handleNameChange}
                onPaste={handlePaste}
                onBlur={handleNameBlur}
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? ERROR_ID : undefined}
                data-testid="playlist-name-input"
                className="col-start-1 row-start-1 w-full min-w-0 -ml-2 pl-2 pr-9 py-1 bg-transparent border-0 squircle-sm text-heading md:text-heading-lg font-semibold text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-elevated-dark-1 focus:bg-gray-100 dark:focus:bg-elevated-dark-1 focus:outline-none focus:ring-2 focus:ring-primary-500 truncate transition-colors"
              />
              <Pencil
                className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500 opacity-60 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity pointer-events-none"
                aria-hidden="true"
              />
            </div>
            {error ? (
              <p id={ERROR_ID} role="alert" className={`mt-1 text-body-sm ${TONE_CLASSES.error}`}>{error}</p>
            ) : (
              <p className="mt-1 text-body-sm text-gray-600 dark:text-gray-400">{COPY.subtitle}</p>
            )}
            {deliveredCaption && (
              <p className={`mt-1 flex items-center gap-1.5 text-caption font-medium ${TONE_CLASSES.success}`} data-testid="playlist-delivered-at">
                <CheckCircle2 className="w-3.5 h-3.5" aria-hidden="true" />
                {deliveredCaption}
              </p>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-start gap-4">
          <div className="flex flex-col gap-1">
            <label htmlFor="playlist-date-input" className="text-caption text-gray-500 dark:text-gray-400">
              {COPY.dateLabel}
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 dark:text-gray-400 pointer-events-none" aria-hidden="true" />
              <input
                id="playlist-date-input"
                type="date"
                value={serviceDate ?? ''}
                readOnly={readOnly}
                onChange={handleDateChange}
                data-testid="playlist-date-input"
                className="pl-9 pr-3 py-2.5 squircle-sm bg-gray-100 dark:bg-elevated-dark-1 border-0 text-body-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 [color-scheme:light] dark:[color-scheme:dark]"
              />
            </div>
          </div>
          {savedControls}
          {deliverControl}
        </div>
      </div>
    </Card>
  );
}
